import { __decorate, __metadata, __awaiter } from 'tslib';
import { InjectorService } from '@tencent/ioc';
import { AdapterCallback } from '../../controllers/adapter/adapter-callback.js';
import { Service } from '../../decorators/class/service.js';
import { FeatureRegistry } from '../../registries/feature-registry.js';
import { BuiltinUiState } from '../store/builtin-state.js';
import { StateManager } from '../store/state-manager.js';
import { SurfaceService } from '../surface/surface-service.js';
import { IWorkbench } from './workbench.interface.js';
import { isServer } from '../../utils/env.js';

/**
 * Workbench 操作器，用于开启编辑器
 *
 * @public
 */
let WorkbenchService = class WorkbenchService {
    constructor(surfaceService, store) {
        this.surfaceService = surfaceService;
        this.store = store;
    }
    // public async open<T>(adapter: Type<IAdapter<T>>, options: OpenEditorOptions);
    // public async open<T>(adapter: Type<IAdapter<T>>, options: OpenAsyncOptions);
    open(adapter, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const instance = InjectorService.get(adapter);
            const ssrChildren = (_a = instance.renderEditorContentToString) === null || _a === void 0 ? void 0 : _a.call(instance);
            let surfaceConfig;
            const dom = document.createElement('div');
            if (options.mode || options.target || options.floatStyle) {
                surfaceConfig = this.surfaceService.createSync(options.mode, options.target, options.floatStyle, ssrChildren);
            }
            else {
                const modeOption = (_b = options.modeOptions) !== null && _b !== void 0 ? _b : { gridType: 'horizontal' };
                surfaceConfig = this.surfaceService.createSync(modeOption, ssrChildren);
            }
            if (!surfaceConfig) {
                return Promise.reject();
            }
            const { id } = surfaceConfig;
            // TODO[ziofatli]: will be delete in v2.0, attachEditor should pass this value
            dom.setAttribute('data-surface-id', id);
            dom.setAttribute('style', 'height: 100%');
            const callbacks = new AdapterCallback(id);
            let editor;
            if (!isServer) {
                editor = yield instance.attachEditor(dom, callbacks, options.content);
            }
            else if (instance.getServerSideEditor) {
                editor = yield instance.getServerSideEditor();
            }
            this.surfaceService.addEditor(id, editor, instance);
            this.appendEditorDomAndFocus(surfaceConfig, options, dom);
            this.callOpenEditorHookForFeatures(instance, editor);
            callbacks.ready();
            return { id, editor };
        });
    }
    callOpenEditorHookForFeatures(instance, editor) {
        FeatureRegistry.forEach((feature) => {
            if (feature.adapters.includes(instance.toString()) && this.isFeatureEnabled(feature) && feature.mounted) {
                feature.onOpen();
                InjectorService.emitAt('$onEditorOpen', feature.provide, editor);
            }
        });
    }
    appendEditorDomAndFocus(surfaceConfig, options, editorDom) {
        if (!options.noFocus) {
            this.surfaceService.setCurrent(surfaceConfig.id);
        }
        surfaceConfig.stream.asObservable()
            .subscribe((dom) => {
            if (dom.firstElementChild !== editorDom) {
                dom.appendChild(editorDom);
                if (!options.noFocus) {
                    this.restoreFocus(surfaceConfig);
                }
            }
            surfaceConfig.dom = dom;
        });
    }
    restoreFocus(surfaceConfig) {
        var _a;
        const adapter = this.surfaceService.getAdapter(surfaceConfig.id);
        const editor = this.surfaceService.getEditor(surfaceConfig.id);
        // 第一次渲染
        if (!surfaceConfig.dom) {
            adapter === null || adapter === void 0 ? void 0 : adapter.requestFocus(editor);
            return;
        }
        const current = (_a = this.surfaceService.getCurrent()) === null || _a === void 0 ? void 0 : _a.id;
        // 解决如wrapWidget造成的surface重渲染丢失焦点
        if (current && current === surfaceConfig.id && surfaceConfig.focusStream.getValue() === true) {
            adapter === null || adapter === void 0 ? void 0 : adapter.requestFocus(editor);
        }
    }
    openSync(adapter, options) {
        const surfaceService = InjectorService.get(SurfaceService);
        const instance = InjectorService.get(adapter);
        const id = options.surface;
        const { dom } = surfaceService.get(id);
        if (!dom) {
            throw Error('Surface dom is not exist');
        }
        const callbacks = new AdapterCallback(id);
        const editor = instance.attachEditor(dom, callbacks, options.content);
        surfaceService.addEditor(id, editor, instance);
        if (!options.noFocus) {
            instance.requestFocus(editor);
            surfaceService.onFocus(id);
        }
        this.callOpenEditorHookForFeatures(instance, editor);
        callbacks.ready();
        return { id, editor };
    }
    close(surfaceId) {
        const surfaceService = InjectorService.get(SurfaceService);
        const editor = surfaceService.getEditor(surfaceId);
        const adapter = surfaceService.getAdapter(surfaceId);
        if (!editor || !adapter)
            return;
        FeatureRegistry.forEach((feature) => {
            // onClose状态无视开关，只关心当前feature有没有被关闭过
            if (feature.isNotClosed() && feature.adapters.includes(adapter.toString())) {
                feature.onClose();
                InjectorService.emitAt('$onEditorClose', feature.provide, editor);
            }
        });
        surfaceService.close(surfaceId);
    }
    isFeatureEnabled(featureProvider) {
        var _a, _b, _c;
        const name = (_a = featureProvider.name) !== null && _a !== void 0 ? _a : featureProvider.className;
        const isFeatureProviderDisabled = (_c = (name
            && ((_b = this.store.getValue(BuiltinUiState.FeatureStatus)) === null || _b === void 0 ? void 0 : _b.has(name)))) !== null && _c !== void 0 ? _c : false;
        return !isFeatureProviderDisabled;
    }
};
WorkbenchService = __decorate([
    Service({ provide: IWorkbench }),
    __metadata("design:paramtypes", [SurfaceService,
        StateManager])
], WorkbenchService);

export { WorkbenchService };
//# sourceMappingURL=workbench-service.js.map
